/* eslint-disable @typescript-eslint/no-explicit-any */
// Chart.js annotation plugin has complex types, using any for pragmatic reasons

import React, { useMemo } from 'react';
import type { ChartOptions, ChartData } from 'chart.js';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import type { ELOChartDataPoint } from '@/types';
import { getTeamColors } from '../../utils/colorUtil';

interface LineDatasetWithAxis {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  borderWidth: number;
  pointBackgroundColor: string;
  pointBorderColor: string;
  pointBorderWidth: number;
  pointRadius: number;
  pointHoverRadius: number;
  pointHitRadius: number;
  pointHoverBackgroundColor: string;
  pointHoverBorderColor: string;
  tension: number;
  fill: boolean;
  yAxisID?: string;
}

// 注册 Chart.js 组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface ELOChartProps {
  data: ChartData<'line'> | ELOChartDataPoint[];
  height?: number;
  className?: string;
  showRank?: boolean; // 是否同时显示排名线 (仅对ELOChartDataPoint[]格式有效)
  showPoints?: boolean; // 是否显示数据点，默认true
}

const ELOChart: React.FC<ELOChartProps> = ({
  data,
  height = 300,
  className = '',
  showRank = false,
  showPoints = true,
}) => {
  const tension = 0; // 曲线张力，0为直线，1为非常弯曲的线
  // 检查数据格式并准备图表数据
  // 动态点大小计算函数
  const calculateDynamicPointSize = (dataLength: number) => {
    if (dataLength < 10) return 4;
    if (dataLength < 30) return 3;
    // if (dataLength < 60) return 2.5;
    // if (dataLength < 100) return 1;
    // if (dataLength < 150) return 0;
    return 2.5;
  };

  const chartData = useMemo(() => {
    // 如果已经是Chart.js标准格式，使用专业颜色系统增强
    if (
      data &&
      typeof data === 'object' &&
      'datasets' in data &&
      !Array.isArray(data)
    ) {
      const chartjsData = data as ChartData<'line'>;
      const colors = getTeamColors(chartjsData.datasets.length, true);

      // 计算动态点大小
      const dataLength = chartjsData.datasets[0]?.data?.length || 0;
      const dynamicPointRadius = showPoints
        ? calculateDynamicPointSize(dataLength)
        : 0;
      const hoverRadius = showPoints ? dynamicPointRadius + 2 : 2;

      // 增强现有数据集的样式
      const enhancedDatasets = chartjsData.datasets.map((dataset, index) => ({
        ...dataset,
        borderColor: colors[index],
        backgroundColor: colors[index] + '20', // 20% opacity
        borderWidth: 2.5,
        pointBackgroundColor: colors[index],
        pointBorderColor: colors[index],
        pointBorderWidth: 0,
        pointRadius: dynamicPointRadius,
        pointHoverRadius: hoverRadius,
        pointHitRadius: 20, // 增加命中半径到20像素
        pointHoverBackgroundColor: colors[index],
        pointHoverBorderColor: colors[index],
        tension: tension,
        fill: false,
        spanGaps: false,
      }));

      return {
        ...chartjsData,
        datasets: enhancedDatasets,
      };
    }

    // 处理ELOChartDataPoint[]格式
    if (!Array.isArray(data)) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const pointsData = data as ELOChartDataPoint[];
    const labels = pointsData.map(point => {
      const date = new Date(point.date);
      return date.toLocaleDateString('zh-CN', {
        month: 'short',
        day: 'numeric',
      });
    });

    const datasetCount = showRank ? 2 : 1;
    const colors = getTeamColors(datasetCount, true);

    // 计算动态点大小
    const dynamicPointRadius = showPoints
      ? calculateDynamicPointSize(pointsData.length)
      : 0;
    const hoverRadius = showPoints ? dynamicPointRadius + 2 : 2;

    const datasets = [
      {
        label: 'ELO 等级分',
        data: pointsData.map(point => point.elo),
        borderColor: colors[0],
        backgroundColor: colors[0] + '20', // 20% opacity
        borderWidth: 2.5,
        pointBackgroundColor: colors[0],
        pointBorderColor: colors[0],
        pointBorderWidth: 0,
        pointRadius: dynamicPointRadius,
        pointHoverRadius: hoverRadius,
        pointHitRadius: 20, // 增加命中半径到20像素
        pointHoverBackgroundColor: colors[0],
        pointHoverBorderColor: colors[0],
        tension: tension,
        fill: false,
      },
    ];

    // 如果需要显示排名线，添加第二个数据集
    if (showRank) {
      const rankPointRadius = showPoints ? 3 : 0;
      const rankHoverRadius = showPoints ? 5 : 2;

      datasets.push({
        label: '排名',
        data: pointsData.map(point => point.rank),
        borderColor: colors[1],
        backgroundColor: colors[1] + '20',
        borderWidth: 2,
        pointBackgroundColor: colors[1],
        pointBorderColor: colors[1],
        pointBorderWidth: 0,
        pointRadius: rankPointRadius,
        pointHoverRadius: rankHoverRadius,
        pointHitRadius: 20, // 增加命中半径到20像素
        pointHoverBackgroundColor: colors[1],
        pointHoverBorderColor: colors[1],
        tension: tension,
        fill: false,
        yAxisID: 'y1', // 使用右侧Y轴
      } as LineDatasetWithAxis);
    }

    return {
      labels,
      datasets,
    };
  }, [data, showRank, showPoints]);

  // 检测是否为时间序列数据
  const isTimeSeriesData = useMemo(() => {
    // 检测是否为时间序列数据（Chart.js格式且数据点为{x, y}结构）
    if (
      data &&
      typeof data === 'object' &&
      'datasets' in data &&
      !Array.isArray(data)
    ) {
      const chartjsData = data as ChartData<'line'>;
      const firstDataset = chartjsData.datasets[0];
      const firstDataPoint = firstDataset?.data?.[0];
      return (
        firstDataPoint &&
        typeof firstDataPoint === 'object' &&
        'x' in firstDataPoint
      );
    }
    return false;
  }, [data]);

  // 二分查找最近数据点的辅助函数（假设数据已按x排序）
  const findNearestPoint = (
    targetX: number,
    dataPoints: Array<{ x: number; y: number }>
  ) => {
    if (!dataPoints.length) return null;

    // 二分查找
    let left = 0;
    let right = dataPoints.length - 1;
    let closest = dataPoints[0];
    let minDistance = Math.abs(dataPoints[0].x - targetX);

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const midPoint = dataPoints[mid];
      const distance = Math.abs(midPoint.x - targetX);

      if (distance < minDistance) {
        minDistance = distance;
        closest = midPoint;
      }

      if (midPoint.x === targetX) {
        return midPoint; // 精确匹配
      } else if (midPoint.x < targetX) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    // 检查边界点
    if (left < dataPoints.length) {
      const leftDistance = Math.abs(dataPoints[left].x - targetX);
      if (leftDistance < minDistance) {
        minDistance = leftDistance;
        closest = dataPoints[left];
      }
    }

    if (right >= 0) {
      const rightDistance = Math.abs(dataPoints[right].x - targetX);
      if (rightDistance < minDistance) {
        minDistance = rightDistance;
        closest = dataPoints[right];
      }
    }

    return closest;
  };

  // 图表配置选项
  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          align: 'start' as const,
          labels: {
            color: '#374151', // darker text for better readability
            font: {
              size: 12,
              weight: 'normal',
              family:
                'PingFang SC, Microsoft YaHei, Helvetica Neue, sans-serif',
            },
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 16,
            boxWidth: 8,
            boxHeight: 8,
          },
        },
        annotation: {
          annotations: {
            ...({
              background2000low: {
                type: 'box' as const,
                yMin: (context: any) => context.chart.scales.y.min,
                yMax: 2000,
                backgroundColor: '#44444408',
                borderWidth: 0,
                display: (context: any) => context.chart.scales.y.min < 2000,
              },
              background2000: {
                type: 'box' as const,
                yMin: 2200,
                yMax: 2400,
                backgroundColor: '#00c95108',
                borderWidth: 0,
                display: (context: any) => context.chart.scales.y.max > 2200,
              },
              background2400: {
                type: 'box' as const,
                yMin: 2400,
                yMax: 2600,
                backgroundColor: '#2b7fff08',
                borderWidth: 0,
                display: (context: any) => context.chart.scales.y.max > 2400,
              },
              background2600: {
                type: 'box' as const,
                yMin: 2600,
                yMax: 2700,
                backgroundColor: '#fbbf2408',
                borderWidth: 0,
                display: (context: any) => context.chart.scales.y.max > 2600,
              },
              background2700: {
                type: 'box' as const,
                yMin: 2700,
                yMax: 2800,
                backgroundColor: '#f59e0b15',
                borderWidth: 0,
                display: (context: any) => context.chart.scales.y.max > 2700,
              },
              background2800: {
                type: 'box' as const,
                yMin: 2800,
                yMax: (context: any) => context.chart.scales.y.max,
                backgroundColor: '#f0b10022',
                borderWidth: 0,
                display: (context: any) => context.chart.scales.y.max > 2800,
              },
            } as any),
            ...{
              baseline2000: {
                type: 'line' as const,
                yMin: 2000,
                yMax: 2000,
                borderColor: '#2b2b2ba0',
                borderWidth: 1,
                borderDash: [2, 2],
              },
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)', // text-primary with opacity
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          cornerRadius: 8,
          titleFont: {
            size: 14,
            weight: 'bold',
          },
          bodyFont: {
            size: 13,
          },
          filter: (tooltipItem, index, tooltipItems) => {
            // 对时间序列数据，确保每个数据集只出现一次且有有效数据
            if (isTimeSeriesData) {
              // 1. 检查数据是否有效
              const targetX = tooltipItem.parsed.x;
              const dataset = tooltipItem.dataset;
              if (dataset.data && Array.isArray(dataset.data)) {
                const dataPoints = dataset.data as Array<{
                  x: number;
                  y: number;
                }>;
                const nearest = findNearestPoint(targetX, dataPoints);
                if (nearest === null) {
                  return false; // 数据无效，不显示
                }
              } else {
                return false;
              }

              // 2. 确保每个数据集只显示第一次出现的项
              const datasetIndex = tooltipItem.datasetIndex;
              const firstOccurrence = tooltipItems.findIndex(
                item => item.datasetIndex === datasetIndex
              );
              return index === firstOccurrence;
            }
            return true;
          },
          callbacks: {
            title: context => {
              if (context.length > 0) {
                const timestamp = context[0].parsed.x;
                const date = new Date(timestamp);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              }
              return '';
            },
            label: context => {
              // 如果是时间序列数据，显示当前选手的最近数据
              if (isTimeSeriesData) {
                const targetX = context.parsed.x;
                const dataset = context.dataset;
                if (dataset.data && Array.isArray(dataset.data)) {
                  const dataPoints = dataset.data as Array<{
                    x: number;
                    y: number;
                  }>;
                  const nearest = findNearestPoint(targetX, dataPoints);
                  if (nearest) {
                    return `${dataset.label}: ${nearest.y.toLocaleString()}`;
                  }
                }
                return '';
              }

              // 默认行为（非时间序列数据）
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              if (label === 'ELO 分数') {
                return `${label}: ${value.toLocaleString()}`;
              } else if (label === '排名') {
                return `${label}: #${value}`;
              }
              return `${label}: ${value}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear' as const,
          display: true,
          grid: {
            color: '#E5E7EB', // border color
            drawBorder: false,
          },
          ticks: {
            color: '#6B7280', // text-secondary
            font: {
              size: 12,
            },
            callback: function (value) {
              const date = new Date(value as number);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            },
          },
        },
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          grid: {
            color: '#E5E7EB',
            drawBorder: false,
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 12,
            },
            callback: value => {
              return (value as number).toLocaleString();
            },
          },
          title: {
            display: true,
            text: 'ELO 等级分',
            color: '#4B5563',
            font: {
              size: 12,
              weight: 'normal',
            },
          },
        },
        ...(showRank && {
          y1: {
            type: 'linear' as const,
            display: true,
            position: 'right' as const,
            grid: {
              drawOnChartArea: false, // 不显示网格线
            },
            ticks: {
              color: '#6B7280',
              font: {
                size: 12,
              },
              callback: value => {
                return `#${value}`;
              },
            },
            title: {
              display: true,
              text: '排名',
              color: '#4B5563',
              font: {
                size: 12,
                weight: 'normal',
              },
            },
            reverse: true, // 排名数字小的在上方
          },
        }),
      },
      interaction: {
        mode: isTimeSeriesData ? ('x' as const) : ('index' as const),
        intersect: false,
      },
      hover: {
        mode: isTimeSeriesData ? ('x' as const) : ('index' as const),
        intersect: false,
      },
    }),
    [showRank, isTimeSeriesData]
  );

  // 数据验证
  const isValidData = useMemo(() => {
    if (!data) return false;

    // Chart.js标准格式验证
    if (
      typeof data === 'object' &&
      'datasets' in data &&
      !Array.isArray(data)
    ) {
      return data.datasets && data.datasets.length > 0;
    }

    // ELOChartDataPoint[]格式验证
    return Array.isArray(data) && data.length > 0;
  }, [data]);

  if (!isValidData) {
    return (
      <div
        className={`flex items-center justify-center bg-white border border-border rounded-xl ${className}`}
        style={{ height }}
      >
        <p className='text-text-secondary'>暂无图表数据</p>
      </div>
    );
  }

  return (
    <div
      className={`bg-white border border-border rounded-xl p-6 shadow-card ${className}`}
    >
      <div style={{ height }}>
        <Line
          data={chartData}
          options={chartOptions}
          key={`chart-${JSON.stringify(chartData).slice(0, 50)}`}
        />
      </div>
    </div>
  );
};

export default ELOChart;
